import ActiveOverlay from 'app/components/partials/active-overlay';
import Audio from './audio';
import Button from './button';
import CallToAction from './call-to-action';
import Carousel from './carousel';
import classnames from 'classnames';
import ColumnCallToAction from './column-call-to-action';
import ContentSet from './content-set';
import DualTab from './dual-tab';
import Encounters from './encounters.container';
import Faq from './faq';
import FileDownload from './file-download';
import FilterStories from './filter-stories';
import Flexi from './flexi';
import Gallery from './gallery';
import Introduction from './introduction';
import Jotform from './jotform';
import NewsShortVideo from './news-short-video';
import OpeningHours from './opening-hours.jsx';
import PagebreakQuote from './pagebreak-quote';
import { POST_MESSAGE_TYPE } from 'app/utilities/post-message-type';
import Pricing from './pricing';
import PropTypes from 'prop-types';
import Rezdy from './rezdy';
import RollingText from './rolling-text';
import StacklaTabs from './stackla-tabs';
import StacklaWidget from './stackla-widget';
import TextContent from './text-content';
import TicketCombo from './ticket-combo';
import { useLocation } from 'react-router-dom';
import Video from './video';
import env, { isProd } from 'config/env';
import React, { useEffect, useRef, useState } from 'react';

const renderBlock = (block, index, hasListing) => {
    switch (block.type) {
        case 'animaldetailstab':
        case 'dualtab':
            return <DualTab key={index} {...block} />;
        case 'audio':
            return <Audio key={index} {...block.data} />;
        case 'button':
            return <Button key={index} {...block.data} />;
        case 'calltoaction':
            return <CallToAction key={index} {...block} />;
        case 'carousel':
            return <Carousel key={index} items={block.data} />;
        case 'content':
        case 'contentaside':
        case 'contentquote':
        case 'stackladirectuploader':
            return <TextContent key={index} {...block} />;
        case 'contentset':
        case 'crosspromo':
            return <ContentSet key={index} {...block.data} />;
        case 'encounters':
            return <Encounters key={index} initialData={block.data} />;
        case 'faq':
            return <Faq key={index} items={block.data} />;
        case 'file':
            return <FileDownload key={index} { ...block.data } />;
        case 'filterstories':
            // The listing is one piece of store state, so a page that lists
            // its own articles leaves the block out rather than standing a
            // second listing on the page for the two to pull against
            return hasListing ? null : <FilterStories key={index} {...block.data} />;
        case 'flexi':
            return <Flexi key={index} items={block.data} />;
        case 'gallery':
            return <Gallery key={index} items={block.data} />;
        case 'introduction':
            return <Introduction key={index} {...block} />;
        case 'newsshortvideo':
            return <NewsShortVideo key={index} {...block} />;
        case 'stackla':
            return <StacklaWidget key={index} {...block.data} />;
        case 'stacklatabs':
            return <StacklaTabs key={index} {...block.data} />;
        case 'ticketpricing':
            return <Pricing key={index} data={block.data} />;
        case 'video':
            return <Video key={index} {...block} />;
        case 'pagebreakquote':
            return <PagebreakQuote key={index} {...block.data} />;
        case 'columncalltoaction':
            return <ColumnCallToAction key={index} {...block.data} />;
        case 'rezdywidget':
            return <Rezdy key={index} {...block.data} />;
        case 'rollingtextzoo':
            return <RollingText key={index} { ...block } />;
        case 'jotform':
            return <Jotform key={index} {...block.data} />;
        case 'ticketcombo':
            return <TicketCombo key={index} {...block.data} />;
        case 'openinghours':
            return <OpeningHours key={index} {...block.data} />;
        default:
            if (isProd) {
                return null;
            }

            return (
                <div key={index} className="constrain-width large" style={{ textAlign: 'center' }}>
                    <p>Missing content block type: &ldquo;{block.type}&rdquo;</p>
                </div>
            );
    }
};

const ContentBlocks = ({ data, hasListing, renderBefore }) => {
    const location = useLocation();
    const isLivePreview = location.pathname.startsWith('/livepreview/');
    const [activeValue, setActiveValue] = useState(null);
    const contentBlocksRef = useRef(null);

    // A page with nothing on it comes back with its blocks as null rather
    // than as an empty collection, and a default prop only stands in for a
    // prop that was not passed at all
    const blocks = data || [];

    const isActive = (index) => isLivePreview && activeValue !== null && activeValue !== undefined && activeValue.toString() === index.toString();

    const handleClick = (index) => {
        if (isLivePreview) {
            // Toggle the active value
            const indexToSend = activeValue !== null && activeValue === index ? null : index;

            setActiveValue(indexToSend);
            // Send data to parent element
            window.parent.postMessage({
                type: POST_MESSAGE_TYPE.ACTIVE_CONTENT_BLOCK_CLICKED,
                payload: {
                    activeIndex: indexToSend
                }
            }, env.CMS_DOMAIN);
        }
    };

    useEffect(() => {
        if (isLivePreview && activeValue !== null && contentBlocksRef.current) {
            const activeBlock = contentBlocksRef.current.querySelector(`[data-block-index="${activeValue}"]`);

            if (activeBlock) {
                activeBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [isLivePreview, activeValue]);

    useEffect(() => {
        // Add event listener for messages from the parent window for Live Preview
        if (isLivePreview) {
            const messageHandler = (event) => {
                // Make sure to validate the origin for security
                const allowedOrigins = [env.CMS_DOMAIN];

                if (!allowedOrigins.includes(event.origin)) {
                    return;
                }

                const message = event.data;

                // The CMS tells us which block is selected in its sidebar
                if (message && message.type === POST_MESSAGE_TYPE.UPDATE_HIGHLIGHTED_ITEM) {
                    const { activeIndex } = message.payload || {};

                    setActiveValue(activeIndex);
                }
            };

            window.addEventListener('message', messageHandler);

            // Clean up event listener on component unmount
            return () => {
                window.removeEventListener('message', messageHandler);
            };
        }
    }, [isLivePreview]);

    if (!blocks.length && !renderBefore) {
        return null;
    }

    return (
        <div className="content-blocks" ref={contentBlocksRef}>
            {renderBefore && renderBefore}
            {blocks.map((block, index) => {
                const isHighlighted = isActive(index);
                const contentBlockContainerClass = classnames('content-block-container', {
                    'is-live-preview': isLivePreview,
                    'is-highlighted': isHighlighted
                });

                return isLivePreview ? (
                    <div
                        key={index}
                        className={contentBlockContainerClass}
                        onClick={() => handleClick(index)}
                        data-block-index={index}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleClick(index);
                            }
                        }}
                    >
                        {isHighlighted && <ActiveOverlay type={block.type} activeIndex={index} />}
                        {renderBlock(block, index, hasListing)}
                    </div>
                ) : renderBlock(block, index, hasListing);
            })}
        </div>
    );
};

ContentBlocks.propTypes = {
    renderBefore: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.array
    ]),
    data: PropTypes.array,
    // Whether the page already renders the news listing itself, which is what
    // makes a listing block on it a duplicate
    hasListing: PropTypes.bool,
    isLarge: PropTypes.bool,
    isNews: PropTypes.bool
};

ContentBlocks.defaultProps = {
    data: [],
    hasListing: false,
    isLarge: false,
    isNews: false
};

export default ContentBlocks;
